import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import * as TableConstants from 'notebook/tables/TableConstants';

const {TABLE_ROW_HEIGHT} = TableConstants;

interface DraggableRowInserterProps {
  tableContainerSelector: string;
  addRows: (numRowsToAdd: number) => void;
  messageOverlayDuringResize?:
    | ((params: {
        numRowsToAdd: number;
        initialNumRows: number;
        initialNumColumns: number;
        canContinueAddingRows: boolean;
      }) => string)
    | null;
  initialNumColumns: number;
  initialNumRows: number;
  maxTotalRows?: number;
}

interface State {
  numRowsToAdd: number;
}

function defaulMessageOverlayDuringResize(params: {
  numRowsToAdd: number;
  initialNumRows: number;
  initialNumColumns: number;
  canContinueAddingRows: boolean;
}): string {
  return `${params.numRowsToAdd + params.initialNumRows} rows ${
    params.canContinueAddingRows ? '' : '(max) '
  }x ${params.initialNumColumns} columns`;
}

export default class DraggableRowInserter extends React.Component<DraggableRowInserterProps, State> {
  static propTypes = {
    tableContainerSelector: PropTypes.string.isRequired,
    addRows: PropTypes.func.isRequired,
    initialNumColumns: PropTypes.number.isRequired,
    initialNumRows: PropTypes.number.isRequired,
    maxTotalRows: PropTypes.number,
  };

  state: State = {
    numRowsToAdd: 0,
  };

  containerDOMNode?: Element;
  tableDOMNode?: Element;

  componentDidMount(): void {
    this.containerDOMNode = $(ReactDOM.findDOMNode(this)!).closest(this.props.tableContainerSelector)[0];
    this.tableDOMNode = $(this.containerDOMNode).find('.mediocre-tableEditable-table--main')[0];
    window.addEventListener('mouseup', this.commitNewTableSize);
    window.addEventListener('mousemove', this.resizeInfoOverlay);
    window.addEventListener('mousewheel', this.resizeInfoOverlay);
  }

  componentWillUnmount(): void {
    window.removeEventListener('mouseup', this.commitNewTableSize);
    window.removeEventListener('mousemove', this.resizeInfoOverlay);
    window.removeEventListener('mousewheel', this.resizeInfoOverlay);
  }

  resizeInfoOverlay = (e: MouseEvent): void => {
    if (!this.tableDOMNode) {
      return;
    }
    const bottomOfTableY = $(this.tableDOMNode).offset()!.top + $(this.tableDOMNode).height();
    const height = Math.max(0, e.pageY - bottomOfTableY);
    const numRowsToAdd = Math.round(height / TABLE_ROW_HEIGHT);
    if (numRowsToAdd > this.state.numRowsToAdd && !this.canContinueAddingRows()) {
      return;
    }
    this.setState({numRowsToAdd});
  };

  commitNewTableSize = (e: MouseEvent): void => {
    if (!this.tableDOMNode) {
      return;
    }
    this.props.addRows(this.state.numRowsToAdd);
  };

  renderInfoOverlay(): JSX.Element {
    const {numRowsToAdd} = this.state;
    const width = $(this.tableDOMNode!).width();
    const tableHeight = $(this.tableDOMNode!).height();
    const height = tableHeight + TABLE_ROW_HEIGHT * numRowsToAdd;
    const messageOverlayFn = this.props.messageOverlayDuringResize || defaulMessageOverlayDuringResize;

    const dimensionsText = messageOverlayFn({
      numRowsToAdd,
      initialNumRows: this.props.initialNumRows,
      initialNumColumns: this.props.initialNumColumns,
      canContinueAddingRows: this.canContinueAddingRows(),
    });
    return (
      // We restrict the width of draggableRowInserter-infoOverlay-dimensionsWrapper because the
      <div className="draggableRowInserter-infoOverlay" style={{width, height, marginTop: -tableHeight}}>
        {
          // table can be more than 100% of the the container, but we want to ensure that the
          // dimensions is entirely visible to the user.
          <div
            className="draggableRowInserter-infoOverlay-dimensionsWrapper"
            style={{
              width: $(this.containerDOMNode!).width(),
              top: $(this.containerDOMNode!).height(),
            }}
          >
            <h3 className="draggableRowInserter-infoOverlay-dimensions">{dimensionsText}</h3>
          </div>
        }
      </div>
    );
  }

  /**
   * Returns whether the user may continue dragging downward to add rows.
   */
  private canContinueAddingRows(): boolean {
    return (
      this.props.maxTotalRows === undefined ||
      this.props.initialNumRows + this.state.numRowsToAdd < this.props.maxTotalRows
    );
  }

  render(): JSX.Element {
    return (
      <div className="draggableRowInserter">
        {this.tableDOMNode != null ? this.renderInfoOverlay() : undefined}
      </div>
    );
  }
}
